import React from 'react';
import PropTypes from 'prop-types';
import { i18n } from '@lingui/core';

/**
 * SizeGradientLegend - Легенда для визуализации градуированного размера
 *
 * Отображает концентрические круги разного размера с подписями
 * Структура:
 * - Контейнер с кругами (position: relative)
 * - Каждый круг позиционируется абсолютно и выровнен по нижнему краю (bottom: 0)
 * - Текстовые подписи справа
 */
export const SizeGradientLegend = ({ items, renderConfig }) => {
    const {
        description = null,
        minRadius = 8,
        maxRadius = 40,
    } = renderConfig;
    const actualRadiuses = items.map(item => item.radius);
    const actualMaxRadius = Math.max(...actualRadiuses);
    const actualMinRadius = Math.min(...actualRadiuses);
    const radiuses = actualRadiuses.map(r => {
        if (actualMaxRadius === actualMinRadius) return minRadius;
        const normalized = (r - actualMinRadius) / (actualMaxRadius - actualMinRadius);
        return minRadius + normalized * (maxRadius - minRadius);
    });

    return (
        <div style={{ marginLeft: '10px', marginRight: '10px', marginTop: '10px', marginBottom: '10px' }}>
            <div
                style={{
                    position: 'relative',
                    paddingLeft: `${maxRadius * 2 + 30}px`,
                    minHeight: `${maxRadius * 2}px`,
                }}
            >
                {/* Контейнер с концентрическими кругами */}
                <div
                    style={{
                        position: 'absolute',
                        left: '0',
                        bottom: '0',
                        width: `${maxRadius * 2}px`,
                        height: `${maxRadius * 2}px`,
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {items.map((item, idx) => {
                        const radius = radiuses[idx];
                        const diameter = radius * 2;

                        return (
                            <div
                                key={item.id || item.label || idx}
                                style={{
                                    position: 'absolute',
                                    bottom: '0',
                                    width: `${diameter}px`,
                                    height: `${diameter}px`,
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                    opacity: 1,
                                    border: '1px solid rgba(255, 255, 255, 0.8)',
                                    boxSizing: 'border-box',
                                    transition: 'all 0.2s ease',
                                    zIndex: items.length - idx,
                                }}
                            />
                        );
                    })}
                </div>

                {/* Контейнер с выносками и подписями */}
                {items.map((item, idx) => {
                    // Берём радиус напрямую (не развёрнутый индекс)
                    const radius = radiuses[idx];
                    // Верхняя точка круга находится на высоте radius * 2 от дна
                    const topPointFromBottom = radius * 2;

                    return (
                        <div
                            key={item.id || item.label || idx}
                            style={{
                                position: 'absolute',
                                left: `${maxRadius}px`,
                                bottom: `${topPointFromBottom}px`,
                                display: 'flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap',
                                fontSize: '12px',
                                color: 'rgba(220, 217, 217, 0.9)',
                                height: '1px',
                            }}
                        >
                            {/* Горизонтальная выноска от верхней точки круга */}
                            <div
                                style={{
                                    width: `${maxRadius + 10}px`,
                                    height: '1px',
                                    backgroundColor: 'rgba(220, 217, 217, 0.5)',
                                    marginRight: '10px',
                                    flexShrink: 0,
                                }}
                            />
                            {/* Подпись */}
                            <div style={{ lineHeight: '1' }}>
                                {i18n._(item.label)}
                            </div>
                        </div>
                    );
                })}
            </div>

            {description && (
                <div style={{ padding: '10px' }}>
                    {description}
                </div>
            )}
        </div>
    );
};

SizeGradientLegend.propTypes = {
    items: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.object.isRequired,
        color: PropTypes.string,
    })).isRequired,
    renderConfig: PropTypes.object.isRequired,
};

SizeGradientLegend.defaultProps = {
};
